/*
 * My Templates — brand templates the user saves from the Upload flow. Kept
 * entirely client-side (localStorage) since there's no backend to own a
 * per-user template library yet: "Yours to manage, not your organisation."
 * Stores the full extracted BrandSpec so a saved row can be re-applied
 * synchronously later (setDirectBrand), with no re-download/re-parse.
 */

import { getErrorMessage } from "../../shared/services/connectorErrors";
import { logError, logOfficeError, scrubMessage } from "../../../../../telemetry";
import type { BrandSpec } from "../../../types";

const STORAGE_KEY = "deckorate.applyBrand.myTemplates";
/** A user can keep at most 3 Your Templates (BRD BR-04). */
export const MAX_MY_BRAND_TEMPLATES = 3;
/** Longest name Rename accepts (BRD FR-15). */
export const MAX_TEMPLATE_NAME_LENGTH = 80;
const MY_BRAND_TEMPLATES_CONNECTOR = "deckorate";

export interface MyBrandTemplate {
  readonly id: string;
  readonly name: string;
  readonly brand: BrandSpec;
  /** Row-preview swatches, captured at save time. */
  readonly colors: readonly string[];
  /** ISO timestamp — newest-first ordering. */
  readonly savedAt: string;
}

/** Thrown by save when the user already has MAX_MY_BRAND_TEMPLATES (EC-22). */
export class TemplateLimitError extends Error {
  constructor() {
    super("You have reached the limit of saved templates.");
    this.name = "TemplateLimitError";
  }
}

/** Thrown by save/rename when another template already uses the name (EC-23). */
export class DuplicateTemplateNameError extends Error {
  constructor() {
    super("A template with this name already exists. Enter a different name.");
    this.name = "DuplicateTemplateNameError";
  }
}

export function logMyBrandTemplatesError(reason: string, message: string): void {
  logError(
    "error.connector.failure",
    { type: "MyBrandTemplatesStorageError", scrubbedMessage: scrubMessage(message) },
    { connectorSystemName: MY_BRAND_TEMPLATES_CONNECTOR, reason },
  );
}

// Math.random() here only needs to be unique among this browser's own local
// templates, not unpredictable — it's a localStorage row key, never a
// security token — so the non-cryptographic PRNG is safe.
function generateFallbackId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`; // NOSONAR typescript:S2245
}

function generateId(): string {
  const uuid = globalThis.crypto?.randomUUID?.() ?? generateFallbackId();
  return `local-${uuid}`;
}

/** Light structural check — enough to keep a corrupted/foreign localStorage
 *  value from crashing the list, without deep-validating every BrandSpec field. */
function isValidEntry(value: unknown): value is MyBrandTemplate {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.id === "string" && entry.id.length > 0 &&
    typeof entry.name === "string" && entry.name.length > 0 &&
    typeof entry.brand === "object" && entry.brand !== null &&
    Array.isArray(entry.colors) && entry.colors.every((c) => typeof c === "string") &&
    typeof entry.savedAt === "string"
  );
}

function readAll(): MyBrandTemplate[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidEntry);
  } catch (error) {
    logMyBrandTemplatesError("my-brand-templates-read", getErrorMessage(error, "Couldn't read saved templates from storage."));
    return [];
  }
}

function writeAll(templates: readonly MyBrandTemplate[]): void {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
}

/** Returns every locally-saved template, newest first. Never throws —
 *  degrades to an empty list on any storage/parsing failure. */
export function listMyBrandTemplates(): MyBrandTemplate[] {
  return readAll();
}

function isNameTaken(templates: readonly MyBrandTemplate[], name: string, exceptId?: string): boolean {
  const wanted = name.trim().toLowerCase();
  return templates.some((t) => t.id !== exceptId && t.name.trim().toLowerCase() === wanted);
}

/**
 * Persists a new local template and returns it. Throws on failure (storage
 * unavailable, quota exceeded) so the caller can surface an error instead of
 * silently claiming success.
 */
export function saveMyBrandTemplate(name: string, brand: BrandSpec, colors: readonly string[]): MyBrandTemplate {
  const trimmedName = name.trim() || "Untitled Template";
  const entry: MyBrandTemplate = { id: generateId(), name: trimmedName, brand, colors, savedAt: new Date().toISOString() };
  const existing = readAll();
  if (existing.length >= MAX_MY_BRAND_TEMPLATES) throw new TemplateLimitError();
  if (isNameTaken(existing, trimmedName)) throw new DuplicateTemplateNameError();
  try {
    writeAll([entry, ...existing]);
    return entry;
  } catch (error) {
    const message = getErrorMessage(error, "Couldn't save this template to local storage.");
    logMyBrandTemplatesError("my-brand-templates-save", message);
    throw new Error(message);
  }
}

/** Removes a local template by id. Never throws — a failed removal is
 *  logged but otherwise harmless (the row just stays listed). */
export function removeMyBrandTemplate(id: string): void {
  try {
    writeAll(readAll().filter((t) => t.id !== id));
  } catch (error) {
    logMyBrandTemplatesError("my-brand-templates-remove", getErrorMessage(error, "Couldn't remove this template from storage."));
  }
}

/**
 * Renames a local template and returns the updated entry. Throws
 * DuplicateTemplateNameError when another template already has the name; an
 * empty or unchanged name is a no-op that returns the entry as-is (FR-15).
 */
export function renameMyBrandTemplate(id: string, name: string): MyBrandTemplate | null {
  const all = readAll();
  const current = all.find((t) => t.id === id);
  if (!current) return null;
  const trimmed = name.trim().slice(0, MAX_TEMPLATE_NAME_LENGTH);
  if (!trimmed || trimmed === current.name) return current;
  if (isNameTaken(all, trimmed, id)) throw new DuplicateTemplateNameError();
  const updated: MyBrandTemplate = { ...current, name: trimmed };
  try {
    writeAll(all.map((t) => (t.id === id ? updated : t)));
    return updated;
  } catch (error) {
    const message = getErrorMessage(error, "Couldn't rename this template.");
    logMyBrandTemplatesError("my-brand-templates-rename", message);
    throw new Error(message);
  }
}

/**
 * Overwrites a saved template's brand with the latest Customise changes —
 * only the latest version is kept (BRD BR-03). Returns null if it no longer exists.
 */
export function updateMyBrandTemplate(id: string, brand: BrandSpec, colors: readonly string[]): MyBrandTemplate | null {
  const all = readAll();
  const current = all.find((t) => t.id === id);
  if (!current) return null;
  const updated: MyBrandTemplate = { ...current, brand, colors, savedAt: new Date().toISOString() };
  try {
    writeAll(all.map((t) => (t.id === id ? updated : t)));
    return updated;
  } catch (error) {
    const message = getErrorMessage(error, "Couldn't save changes to this template.");
    logMyBrandTemplatesError("my-brand-templates-update", message);
    throw new Error(message);
  }
}

// ─── Original files behind Your Templates ──────────────────────────────────
// The original .pptx/.potx bytes behind each Your Template, kept in IndexedDB
// (a template can be tens of MB — far beyond localStorage) so Download can
// hand the file back (BRD FR-16). Every call degrades quietly: a host without
// IndexedDB just can't offer Download for that template.

const DB_NAME = "deckorate.applyBrand";
const STORE = "templateFiles";

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") {
        resolve(null);
        return;
      }
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => {
        logOfficeError(request.error, "template-file-store-open-request");
        resolve(null);
      };
    } catch (error) {
      logOfficeError(error, "template-file-store-open");
      resolve(null);
    }
  });
}

function run<T>(mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | null> {
  return openDb().then(
    (db) =>
      new Promise<T | null>((resolve) => {
        if (!db) {
          resolve(null);
          return;
        }
        try {
          const tx = db.transaction(STORE, mode);
          const request = action(tx.objectStore(STORE));
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => {
            logOfficeError(request.error, "template-file-store-request");
            resolve(null);
          };
          tx.oncomplete = () => db.close();
        } catch (error) {
          logOfficeError(error, "template-file-store-run");
          resolve(null);
        }
      }),
  );
}

/** Stores the file behind a saved template. Returns whether it was stored. */
export async function putTemplateFile(id: string, bytes: ArrayBuffer): Promise<boolean> {
  const result = await run("readwrite", (store) => store.put(bytes, id));
  return result !== null;
}

/** The stored file, or null when none was kept. */
export async function getTemplateFile(id: string): Promise<ArrayBuffer | null> {
  const result = await run<ArrayBuffer | undefined>("readonly", (store) => store.get(id));
  return result ?? null;
}

export async function deleteTemplateFile(id: string): Promise<void> {
  await run("readwrite", (store) => store.delete(id));
}

/** File name Download saves under: ".potx" is added when the name has no .pptx or .potx extension (FR-16). */
export function downloadFileName(name: string): string {
  return /\.(pptx|potx)$/i.test(name) ? name : `${name}.potx`;
}
